import { useCallback, useEffect, useMemo, useState } from "react";
import {
  getPendingCommunityReports,
  patchCommunityReportModeration,
} from "../../services/midpointApi";
import { moderationLabel } from "./intelligence/intelligenceLabels";
import StatusBadge from "./intelligence/StatusBadge";
import AdminStatePanel from "./admin/layout/AdminStatePanel";

function moderationTone(status) {
  if (status === "approved") {
    return "confirmed";
  }
  if (status === "rejected") {
    return "disputed";
  }
  if (status === "needs_review" || status === "pending") {
    return "medium";
  }
  return "neutral";
}

const CommunityReportModeration = ({ onClose }) => {
  const [reports, setReports] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [actingOnId, setActingOnId] = useState("");
  const [moderationNotes, setModerationNotes] = useState({});

  const loadReports = useCallback(async () => {
    try {
      setIsLoading(true);
      setErrorMessage("");
      const pending = await getPendingCommunityReports();
      setReports(pending);
    } catch (error) {
      setErrorMessage(error.message || "Unable to load pending community reports.");
      setReports([]);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    loadReports();
  }, [loadReports]);

  const handleModeration = async (reportId, moderationStatus) => {
    try {
      setActingOnId(reportId);
      setErrorMessage("");
      setSuccessMessage("");

      const note =
        moderationNotes[reportId]?.trim() ||
        `Marked ${moderationStatus} from local admin moderation UI.`;

      await patchCommunityReportModeration(reportId, {
        moderationStatus,
        moderationNote: note,
      });

      setModerationNotes((current) => {
        const next = { ...current };
        delete next[reportId];
        return next;
      });

      setSuccessMessage(`Report ${moderationLabel(moderationStatus).toLowerCase()}.`);
      await loadReports();
    } catch (error) {
      setErrorMessage(error.message || "Unable to update report moderation.");
    } finally {
      setActingOnId("");
    }
  };

  const kpis = useMemo(() => {
    const total = reports.length;
    const pending = reports.filter((r) => r.moderationStatus === "pending").length;
    const needsReview = reports.filter((r) => r.moderationStatus === "needs_review").length;
    const types = {};
    reports.forEach((r) => {
      const type = r.reportType?.replace(/_/g, " ") || "unknown";
      types[type] = (types[type] || 0) + 1;
    });

    return { total, pending, needsReview, types };
  }, [reports]);

  return (
    <div className="admin-dashboard admin-moderation-dash">
      <header className="admin-dashboard__header">
        <div>
          <h1 className="admin-dashboard__title">Moderation</h1>
          <p className="admin-dashboard__subtitle">
            Review and moderate community-submitted reports before publishing
          </p>
        </div>
        <div className="admin-dashboard__header-actions">
          <button
            type="button"
            className="admin-dashboard__action-btn"
            onClick={loadReports}
            disabled={isLoading}
          >
            Refresh
          </button>
          {onClose && (
            <button
              type="button"
              className="admin-dashboard__action-btn"
              onClick={onClose}
            >
              ← Dashboard
            </button>
          )}
        </div>
      </header>

      {!isLoading && !errorMessage && (
        <section className="admin-dashboard__kpis">
          <div className="admin-dash__kpi-card">
            <span className="admin-dash__kpi-label">Queue Size</span>
            <span className="admin-dash__kpi-value">{kpis.total}</span>
          </div>
          <div className="admin-dash__kpi-card">
            <span className="admin-dash__kpi-label">Pending</span>
            <span
              className={`admin-dash__kpi-value${kpis.pending > 0 ? " admin-dash__kpi-value--amber" : ""}`}
            >
              {kpis.pending}
            </span>
          </div>
          <div className="admin-dash__kpi-card">
            <span className="admin-dash__kpi-label">Needs Review</span>
            <span
              className={`admin-dash__kpi-value${kpis.needsReview > 0 ? " admin-dash__kpi-value--amber" : ""}`}
            >
              {kpis.needsReview}
            </span>
          </div>
          {Object.entries(kpis.types).map(([type, count]) => (
            <div className="admin-dash__kpi-card" key={type}>
              <span className="admin-dash__kpi-label">{type}</span>
              <span className="admin-dash__kpi-value">{count}</span>
            </div>
          ))}
        </section>
      )}

      {successMessage && (
        <div className="admin-re-toast admin-re-toast--success" role="status">
          {successMessage}
        </div>
      )}

      {isLoading ? <AdminStatePanel variant="loading" message="Loading pending reports…" /> : null}

      {!isLoading && errorMessage ? (
        <AdminStatePanel variant="error" message={errorMessage} onRetry={loadReports} />
      ) : null}

      {!isLoading && !errorMessage && reports.length === 0 ? (
        <section className="admin-dash__card admin-dash__card--wide">
          <div className="admin-geocode-dash__empty">
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#94a3b8" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14" />
              <polyline points="22 4 12 14.01 9 11.01" />
            </svg>
            <p className="muted">No pending or needs-review reports right now.</p>
          </div>
        </section>
      ) : null}

      {!isLoading && !errorMessage && reports.length > 0 ? (
        <div className="admin-moderation-dash__list">
          {reports.map((report) => (
            <article key={report.id} className="admin-dash__card admin-moderation-dash__card">
              <div className="admin-moderation-dash__card-header">
                <div className="admin-moderation-dash__card-title">
                  <StatusBadge
                    label={moderationLabel(report.moderationStatus)}
                    tone={moderationTone(report.moderationStatus)}
                  />
                  <h2>{report.title}</h2>
                </div>
                <div className="admin-moderation-dash__card-meta-row">
                  <span className="admin-canonical-dash__status-pill">
                    {report.reportType?.replace(/_/g, " ")}
                  </span>
                  <span className="muted">
                    {report.displayTime || report.submittedAt}
                  </span>
                </div>
              </div>

              <p className="admin-moderation-dash__description">{report.description}</p>

              <div className="admin-moderation-dash__meta-grid">
                <div>
                  <span className="admin-dash__kpi-label">Area</span>
                  <span>{report.areaName || report.areaId || "—"}</span>
                </div>
                <div>
                  <span className="admin-dash__kpi-label">Status</span>
                  <span>{report.status}</span>
                </div>
                {report.confidenceScore != null && (
                  <div>
                    <span className="admin-dash__kpi-label">Confidence</span>
                    <span>{report.confidenceScore}</span>
                  </div>
                )}
                <div>
                  <span className="admin-dash__kpi-label">Coordinates</span>
                  <span>{report.latitude}, {report.longitude}</span>
                </div>
                {report.displaySource && (
                  <div>
                    <span className="admin-dash__kpi-label">Source</span>
                    <span>{report.displaySource}</span>
                  </div>
                )}
              </div>

              <div className="admin-moderation-dash__resolve">
                <textarea
                  className="admin-review-dash__notes"
                  rows={2}
                  value={moderationNotes[report.id] || ""}
                  onChange={(event) =>
                    setModerationNotes((current) => ({
                      ...current,
                      [report.id]: event.target.value,
                    }))
                  }
                  placeholder="Moderation note (optional)"
                />
                <div className="admin-moderation-dash__actions">
                  <button
                    type="button"
                    className="admin-dashboard__action-btn admin-dashboard__action-btn--primary"
                    disabled={actingOnId === report.id}
                    onClick={() => handleModeration(report.id, "approved")}
                  >
                    Approve
                  </button>
                  <button
                    type="button"
                    className="admin-dashboard__action-btn admin-canonical-dash__action-danger"
                    disabled={actingOnId === report.id}
                    onClick={() => handleModeration(report.id, "rejected")}
                  >
                    Reject
                  </button>
                  <button
                    type="button"
                    className="admin-dashboard__action-btn"
                    disabled={actingOnId === report.id}
                    onClick={() => handleModeration(report.id, "needs_review")}
                  >
                    Needs Review
                  </button>
                  <button
                    type="button"
                    className="admin-dashboard__action-btn"
                    disabled={actingOnId === report.id}
                    onClick={() => handleModeration(report.id, "archived")}
                  >
                    Archive
                  </button>
                </div>
              </div>
            </article>
          ))}
        </div>
      ) : null}
    </div>
  );
};

export default CommunityReportModeration;
